import { useMemo } from 'react';

import { InvestmentPackage, Money as MoneyModel, PackageStats } from 'api-client';

import { formatRoiIrr, sumMoneyAmounts, weightedAverageByInvested } from './packageUtils';

type ComfortStatItem =
  | { label: string; money: MoneyModel; tooltip?: string }
  | { label: string; value: string; tooltip?: string };

export const useComfortAccountStats = (
  packages: InvestmentPackage[],
  packageStats: PackageStats[]
): ComfortStatItem[] => {
  return useMemo(() => {
    if (packageStats.length === 0 && packages.length === 0) {
      return [
        { label: 'БОНУСОВ НАЧИСЛЕНО', value: '—' },
        { label: 'ИНВЕСТИРОВАНО', value: '—' },
        { label: 'ВОЗВРАЩЕНО', value: '—' },
        { label: 'ПРОГНОЗ ВОЗВРАТА', value: '—' },
        {
          label: 'ФАКТ ROI / IRR',
          value: '— / —',
          tooltip: 'Фактическая доходность по счету комфорт',
        },
      ];
    }

    const currencyCode =
      packageStats[0]?.invested.currencyCode ??
      packages[0]?.accruedBonuses.currencyCode ??
      packages[0]?.money.currencyCode ??
      'RUB';

    const accruedBonuses = sumMoneyAmounts(
      packages.map((pkg) => pkg.accruedBonuses),
      currencyCode
    );
    const invested = sumMoneyAmounts(
      packageStats.map((item) => item.invested),
      currencyCode
    );
    const incomePaid = sumMoneyAmounts(
      packageStats.map((item) => item.incomePaid),
      currencyCode
    );
    const forecastItems = packageStats
      .map((item) => item.forecastReturn)
      .filter((money): money is MoneyModel => money != null);
    const forecastReturn =
      forecastItems.length > 0
        ? sumMoneyAmounts(forecastItems, currencyCode)
        : new MoneyModel(0, currencyCode);

    const weightedRoi = weightedAverageByInvested(
      packageStats.map((item) => ({ invested: item.invested, value: item.roi }))
    );
    const weightedXirr = weightedAverageByInvested(
      packageStats.map((item) => ({ invested: item.invested, value: item.xirr }))
    );

    return [
      { label: 'БОНУСОВ НАЧИСЛЕНО', money: accruedBonuses },
      { label: 'ИНВЕСТИРОВАНО', money: invested },
      { label: 'ВОЗВРАЩЕНО', money: incomePaid },
      {
        label: 'ПРОГНОЗ ВОЗВРАТА',
        money: forecastReturn,
      },
      {
        label: 'ФАКТ ROI / IRR',
        value: formatRoiIrr(weightedRoi, weightedXirr),
        tooltip: 'Фактическая доходность по счету комфорт',
      },
    ];
  }, [packages, packageStats]);
};
